// TODO: Reorganize into utils/generationModel

import { isMatching, P } from 'ts-pattern'
import { joinURL } from 'ufo'
import type * as v from 'valibot'
import {
  DEFAULT_DITV1_SIZE,
  DEFAULT_SIZE,
  DEFAULT_XL_SIZE,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/constants'
import {
  type GenerationModelBaseFragment,
  type GenerationModelDetailFragment,
  type GenerationModelPreviewFragment,
  GenerationModelType,
  type GenerationModelVersionBaseFragment,
  type GenerationModelVersionPreviewFragment,
  ModerationStatus,
} from 'src/services/apollo/generated/graphql'
import type { GenerationModelVersionExtra } from 'src/services/helpers/schema/modelVersionExtra'
import type { QualityTagSchema } from 'src/services/helpers/schema/taskParameters'
import { attemptParseJSON } from '.'

export const isSDModelUnion = P.union(
  GenerationModelType.Sd3MediumModel,
  GenerationModelType.SdV1Model,
  GenerationModelType.SdxlModel,
  GenerationModelType.UnknownModel,
)
export const isSDModel = isMatching(isSDModelUnion)

export const isSDV1Model = isMatching(GenerationModelType.SdV1Model)
export const isSD3Model = isMatching(GenerationModelType.Sd3MediumModel)
export const isXLModel = isMatching(GenerationModelType.SdxlModel)
export const isDitV1Model = isMatching(
  P.union(GenerationModelType.Dit7BModel, GenerationModelType.Dit7Model),
)
export const isDitMioModel = isMatching(GenerationModelType.Dit9Model)
export const isMMDIT26AModel = isMatching(GenerationModelType.Mmdit26AModel)
export const isDitV1OrMMDIT26AModel = isMatching(
  P.union(
    GenerationModelType.Dit7BModel,
    GenerationModelType.Dit7Model,
    GenerationModelType.Mmdit26AModel,
  ),
)
export function getModelDefaultSizeDivisor(
  modelType?: GenerationModelType,
): number {
  if (modelType && (isDitV1Model(modelType) || isMMDIT26AModel(modelType))) {
    return 16
  }
  return 8
}

export const isChatModel = isMatching(GenerationModelType.Chat)
export const isVideoModel = isMatching(GenerationModelType.VideoModel)

export const isLoraUnion = P.union(
  GenerationModelType.Lora,
  GenerationModelType.SdxlLora,
  GenerationModelType.UserLora,
  GenerationModelType.UserSdxlLora,
  GenerationModelType.UnknownLora,
  GenerationModelType.MultiLora,
  GenerationModelType.UserMultiLora,
  GenerationModelType.VideoModelLora,
)
export const isLora = isMatching(isLoraUnion)
export const isVideoLora = isMatching(GenerationModelType.VideoModelLora)

export const isUserLoraUnion = P.union(
  GenerationModelType.UserLora,
  GenerationModelType.UserSdxlLora,
  GenerationModelType.UserMultiLora,
)
export const isUserLora = isMatching(isUserLoraUnion)
export const isUserMultiLora = isMatching(GenerationModelType.UserMultiLora)
export const isMultiLora = isMatching(GenerationModelType.MultiLora)
export const isMultiUnionLoraUnion = P.union(
  GenerationModelType.UserMultiLora,
  GenerationModelType.MultiLora,
)
export const isMultiUnionLora = isMatching(isMultiUnionLoraUnion)

export const hasModelTypeDisabledAddVersion = isMatching(
  P.union(GenerationModelType.UserLora, GenerationModelType.UserSdxlLora),
)

export const isModelCompatibleWithVae = isMatching(
  P.union(GenerationModelType.SdV1Model, GenerationModelType.SdxlModel),
)

export const isModelCompatibleWithIPAdapter = isMatching(
  P.union(GenerationModelType.SdV1Model, GenerationModelType.SdxlModel),
)

export function isModelCoverAppealable(
  model?: Pick<GenerationModelDetailFragment, 'flag'>,
): boolean {
  return model?.flag?.isAppealable ?? false
}

export function shouldModelBeBlurred(
  model?: Pick<GenerationModelBaseFragment, 'flag'> | null,
): boolean {
  return model?.flag?.shouldBlur ?? false
}

// Merged into "cover removed" state.
// Not actually useful at the moment.
function isModelCoverUnderReview(
  model?: Pick<GenerationModelDetailFragment, 'flag'>,
): boolean {
  return model?.flag?.status === ModerationStatus.Human
}

export function isModelCoverWarned(
  model?: Pick<GenerationModelDetailFragment, 'flag'>,
): boolean {
  return (isModelCoverUnderReview(model) || model?.flag?.isWarned) ?? false
}

export function isLoraCompatWithModel(
  modelType?: GenerationModelType,
  loraType?: GenerationModelType,
) {
  if (isXLModel(modelType) && isXLModel(loraType)) return true
  if (isSDV1Model(modelType) && isSDV1Model(loraType)) return true
  if (isDitV1Model(modelType) && isDitV1Model(loraType)) return true
  if (isMMDIT26AModel(modelType) && isMMDIT26AModel(loraType)) return true
  return false
}

export function isModelPixaiInhouse(
  curations?: GenerationModelPreviewFragment['curations'],
) {
  if (!curations) return false
  return curations.includes('inhouse')
}

// Compatibilities -- explicit disable
export function modelSupportsLowPriority(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportLowPriority =
    versionExtra?.compatibility?.lowPriority === false
  return !isNotSupportLowPriority
}
export function modelSupportsControlNet(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportControlNet =
    versionExtra?.compatibility?.controlnet === false
  return !isNotSupportControlNet
}
export function modelSupportsUpscale(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportUpscale = versionExtra?.compatibility?.upscale === false
  return !isNotSupportUpscale
}
export function modelSupportsLora(versionExtra?: GenerationModelVersionExtra) {
  const isNotSupportLora = versionExtra?.compatibility?.lora === false
  return !isNotSupportLora
}
export function modelSupportsFaceFix(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportFaceFix =
    versionExtra?.compatibility?.enableADetailer === false
  return !isNotSupportFaceFix
}
export function modelSupportsNegativePrompt(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportNegativePrompt =
    versionExtra?.compatibility?.negativePrompt === false
  return !isNotSupportNegativePrompt
}
export function modelSupportsCfgScale(
  versionExtra?: GenerationModelVersionExtra,
) {
  const isNotSupportCfgScale = versionExtra?.compatibility?.cfgScale === false
  return !isNotSupportCfgScale
}
export function modelSupportsVae(versionExtra?: GenerationModelVersionExtra) {
  const isNotSupportVae = versionExtra?.compatibility?.vae === false
  return !isNotSupportVae
}

// Compatibilities -- explicit enable
export function canModelUseStyleMix(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.styleMix === true
}
export function hasModelEnabledLightning(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.lightning === true
}
export function hasModelEnabledShiftValue(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.shift === true
}
export function hasModelEnabledRescaleCfg(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.rescaleCfg === true
}
// e.g. MMDIT26A_MODEL → mmdit26amodel
export function normalizeModelType(type?: string) {
  return type?.toLowerCase().replace(/_/g, '') ?? ''
}

export function canModelUseStyleKey(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.styleKey === true
}
export function canModelUseWatermark(
  versionExtra?: GenerationModelVersionExtra,
) {
  return versionExtra?.compatibility?.watermark === true
}

export function hasModelEnabledInferenceConfig(
  versionExtra?: GenerationModelVersionExtra,
) {
  return (
    hasModelEnabledRescaleCfg(versionExtra) ||
    hasModelEnabledShiftValue(versionExtra)
  )
}

export const LIGHTNING_MODE_DEFAULT_PARAMS = {
  lightning: true,
  cfgScale: 5.5,
  samplingSteps: 8,
}

/**
 * --- Style Seed Start ---
 */
export const DEFAULT_STYLE_MIX_WEIGHT = 1
export function getRandomStyleMixRaw() {
  return Math.floor(Math.random() * 99999)
}
/**
 * --- Style Seed End ---
 */

/**
 * --- Quality Tag Start ---
 */

export type QualityTag = v.InferInput<typeof QualityTagSchema>

export const PROMPT_IN_QUALITY_TAG = '__PROMPT__'
export const formatQualityTag = (tag: QualityTag) => {
  const prefix = tag.prefix ?? ''
  const suffix = tag.suffix ?? ''
  return [prefix, PROMPT_IN_QUALITY_TAG, suffix].filter(Boolean).join(', ')
}

/**
 * Format quality tag for display
 * @param tag - Quality tag to format
 * @param promptPlaceholder - Text to replace __PROMPT__
 */
export const formatQualityTagForDisplay = (
  tag: QualityTag,
  promptPlaceholder: string,
) => {
  const prefix = tag.prefix ?? ''
  const suffix = tag.suffix ?? ''
  return [prefix, promptPlaceholder, suffix].filter(Boolean).join(', ')
}

export const USED_MODEL_QUALITY_TAGS_STORAGE_KEY =
  'generator::used-model-quality-tags'
/**
 * Receives Version's Extra, NOT Model's Extra
 *
 * @param versionExtra
 */
export function getModelQualityTags(
  versionExtra?: GenerationModelVersionExtra,
) {
  const value = versionExtra?.qualityTags
  if (Array.isArray(value)) return value as QualityTag[]
}
export function canModelUsePresetQualityTags(
  versionExtra?: GenerationModelVersionExtra,
) {
  const tags = getModelQualityTags(versionExtra)
  return Array.isArray(tags) && tags?.length > 0
}
export function retrieveQualityTagFromLocalStorage(modelId?: string) {
  if (!modelId) return undefined
  const storageValue = localStorage.getItem(USED_MODEL_QUALITY_TAGS_STORAGE_KEY)
  const storageObj = attemptParseJSON(storageValue)
  if (storageObj?.tags)
    return (storageObj.tags as Record<string, QualityTag>)?.[modelId]
}
/**
 * --- Quality Tag End ---
 */

export function getDefaultModelForLora(
  modelList?: GenerationModelPreviewFragment[],
  loraType?: GenerationModelType,
) {
  if (isMMDIT26AModel(loraType)) {
    return modelList?.find(it => isMMDIT26AModel(it.type))
  }

  if (isDitV1Model(loraType)) {
    return modelList?.find(it => isDitV1Model(it.type))
  }

  // Select first non-StyleMix XL Model if imported lora is XL
  if (isXLModel(loraType)) {
    return modelList?.find(
      it =>
        it.type === GenerationModelType.SdxlModel &&
        it.latestAvailableVersion?.extra?.compatibility?.styleMix !== true,
    )
  }

  if (isSDV1Model(loraType)) {
    return modelList?.find(it => it.type === GenerationModelType.SdV1Model)
  }
  // Unable to determine, just fallback to the first model
  return modelList?.[0]
}

/**
 * Create a URL for a model page
 */
export function createModelUrl(
  model: string | Pick<GenerationModelBaseFragment, 'id'>,
  full?: boolean,
): string
/**
 * Create a URL for a model version page
 */
export function createModelUrl(
  model: string | Pick<GenerationModelBaseFragment, 'id'>,
  version?: string | Pick<GenerationModelVersionBaseFragment, 'id'>,
  full?: boolean,
): string
// Implementation
export function createModelUrl(
  model: string | Pick<GenerationModelBaseFragment, 'id'>,
  arg1?: boolean | string | Pick<GenerationModelVersionBaseFragment, 'id'>,
  full?: boolean,
): string {
  const modelId = typeof model === 'string' ? model : model.id
  const version = typeof arg1 === 'boolean' ? undefined : arg1
  const versionId = typeof version === 'string' ? version : version?.id
  full = typeof arg1 === 'boolean' ? arg1 : full

  return joinURL(
    full ? window.origin : '',
    `/model/${modelId}`,
    versionId || '',
  )
}

// Check if a model object is a LoRA without a category
export const isLoraCategoryNotSet = isMatching({
  type: P.when(isLora),
  category: P.optional(P.union(P.nullish, '')),
})

// Check if a model object has any tags set
export const isModelLoraTagNotSet = isMatching({
  tags: P.optional(P.union(P.nullish, [])),
})

export function isLoraFeatureEnabled(lora?: Record<string, any>) {
  return lora != null && Object.keys(lora).length > 0
}

/**
 * Default task size for different model types
 * @param usingXLModelSize
 * @returns
 */
export function getDefaultSizeByModelType(modelType?: GenerationModelType) {
  if (isXLModel(modelType)) return DEFAULT_XL_SIZE
  if (isDitV1Model(modelType)) return DEFAULT_DITV1_SIZE
  if (isMMDIT26AModel(modelType)) return DEFAULT_XL_SIZE
  return DEFAULT_SIZE
}

export function getBaseModelTypeFromLoraVersion(
  version?: Pick<GenerationModelVersionBaseFragment, 'loraBaseModelType'>,
) {
  return version?.loraBaseModelType as GenerationModelType | undefined
}

/**
 * If version is composite lora, return the lora base model type
 * Since composite lora type itself isn't useful
 * @param version
 * @returns
 */
export function getModelTypeFromVersion(
  version?: Pick<
    GenerationModelVersionPreviewFragment,
    'modelType' | 'loraBaseModelType'
  >,
) {
  if (isMultiUnionLora(version?.modelType)) {
    return getBaseModelTypeFromLoraVersion(version)
  }
  return version?.modelType
}

/**
 * For composite lora, return the base lora type
 * Since composite lora type itself isn't useful
 * @param version
 * @returns
 */
export function getBaseLoraTypeFromModelVersion(
  version?: Pick<
    GenerationModelVersionPreviewFragment,
    'modelType' | 'loraBaseModelType'
  >,
) {
  // For composite lora
  const modelType = getBaseModelTypeFromLoraVersion(version)
  return modelType
}

type GenerationModelTypeWithSizePreset =
  | typeof GenerationModelType.SdV1Model
  | typeof GenerationModelType.SdxlModel
  | typeof GenerationModelType.Dit7Model
  | typeof GenerationModelType.Mmdit26AModel
/**
 * Function to convert any modelType to one that has known size presets
 *
 * 1. Dit7BModel (Legacy Dit V1 type) -> Dit7Model
 * 2. Chat Model: We don't allow generating with chat models, but fallback to SDXL to prevent crashing. The size returned is not going to be used anyway.
 * 3. SDv1: keep it as-is
 *
 * For other types, always return SDXL
 *
 * @param type
 * @returns
 */
export function parseModelTypeForSizeSelection(
  type?: GenerationModelType,
): GenerationModelTypeWithSizePreset {
  if (type === GenerationModelType.Dit7BModel)
    return GenerationModelType.Dit7Model
  if (type === GenerationModelType.Mmdit26AModel)
    return GenerationModelType.Mmdit26AModel
  if (type === GenerationModelType.Chat) return GenerationModelType.SdxlModel
  if (type === GenerationModelType.SdV1Model)
    return GenerationModelType.SdV1Model
  // Fallback to SDXL for other or unknown model types
  return GenerationModelType.SdxlModel
}

export function normalizeLegacyModelType(type?: GenerationModelType) {
  if (type === GenerationModelType.Dit7BModel)
    return GenerationModelType.Dit7Model
  return type
}

// TODO: [MODEL_PERMISSION] Remove this after we have proper model permission system to handle membership exclusive models
export const PIXAI_REFERENCE_PRO_MODEL_VERSION_ID_PROD = '1948514378441961474'
export const PIXAI_REFERENCE_MODEL_VERSION_ID_PROD = '1917311758307382235'
export const PIXAI_REFERENCE_MODEL_V2_VERSION_ID_PROD = '1983993578828959744'
export const GPT_IMAGE_2_MODEL_VERSION_ID_PROD = '2006468692917575683'

const membershipRequiredModelVersionIds = new Set<string>([
  PIXAI_REFERENCE_PRO_MODEL_VERSION_ID_PROD,
  PIXAI_REFERENCE_MODEL_VERSION_ID_PROD,
  PIXAI_REFERENCE_MODEL_V2_VERSION_ID_PROD,
  GPT_IMAGE_2_MODEL_VERSION_ID_PROD,
])

export const isModelMembershipRequired = (modelVersionId?: string) => {
  return (
    modelVersionId !== undefined &&
    membershipRequiredModelVersionIds.has(modelVersionId)
  )
}

export const isModelClaimed = (
  model?: Pick<GenerationModelBaseFragment, 'extra'>,
): boolean => {
  return model?.extra?.claimed === true
}
